import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTeamsStore } from '@/stores/teamsStore';
import { useServicesStore } from '@/stores/servicesStore';
import { useUsersStore } from '@nimbitlabs/features';
import { usePermissions } from '@nimbitlabs/features';
import { AddTeamMemberModal } from '@/components/AddTeamMemberModal';
import { ManageTeamMemberModal } from '@/components/ManageTeamMemberModal';
import { Team, TeamMember } from '@/types/team';
import { api } from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, ErrorAlert } from '@nimbitlabs/ui';
import { 
  ArrowLeft, 
  Users, 
  Package, 
  Mail, 
  UserPlus, 
  Calendar, 
  Tag, 
  UserCog, 
  Edit, 
  ExternalLink, 
  FileText, 
  GitBranch, 
  MessageSquare, 
  BookOpen, 
  Globe, 
  FolderKanban, 
  Settings as SettingsIcon
} from 'lucide-react';

export const TeamDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  // Zustand stores
  const { teams, getTeamById, addMember, removeMember } = useTeamsStore();
  const { services } = useServicesStore();
  const { users } = useUsersStore();
  
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  useEffect(() => {
    const loadTeam = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const t = await getTeamById(id);
        setTeam(t as Team);
        setError(null);
      } catch (e) {
        setError(e as Error);
      } finally {
        setLoading(false);
      }
    };
    loadTeam();
  }, [id, getTeamById]);
  
  const teamServices = services.filter(s => s.teamId === id);
  
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [manageMemberModalOpen, setManageMemberModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/teams">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Teams
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
            <h3 className="text-lg font-medium mb-2">Loading team...</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/teams">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Teams
            </Link>
          </Button>
        </div>
        <ErrorAlert error={error} />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/teams">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Teams
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Team not found</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleAddMember = async (userId: string, role: string) => {
    if (!id) return;
    
    try {
      await addMember(id, userId, role);
      // Reload team to get updated members
      const updated = await getTeamById(id);
      setTeam(updated as Team);
    } catch (err: any) {
      setError(err);
    }
  };

  const handleUpdateMemberRole = async (updatedMember: TeamMember) => {
    if (!id) return;
    
    try {
      // (Backend doesn't have a dedicated update role endpoint)
      await removeMember(id, updatedMember.id);
      await addMember(id, updatedMember.id, updatedMember.role);
      
      // Reload team to get updated members
      const updated = await getTeamById(id);
      setTeam(updated as Team);
    } catch (error) {
      console.error('Failed to update member:', error);
      setError(error as Error);
    }
  };

  const handleRemoveMember = async (member: TeamMember) => {
    if (!id) return;
    
    try {
      await removeMember(id, member.id);
      // Reload team to get updated members
      const updated = await getTeamById(id);
      setTeam(updated as Team);
    } catch (err: any) {
      setError(err);
    }
  };

  const openManageMemberModal = (member: TeamMember) => {
    setSelectedMember(member);
    setManageMemberModalOpen(true);
  };

  const getRoleColor = (role: string) => {
    const roleLower = role.toLowerCase();
    
    // Check for role keywords to assign colors
    if (roleLower.includes('lead') || roleLower.includes('manager')) {
      return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    }
    if (roleLower.includes('frontend') || roleLower.includes('front-end')) {
      return 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20';
    }
    if (roleLower.includes('backend') || roleLower.includes('back-end')) {
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    }
    if (roleLower.includes('full stack') || roleLower.includes('fullstack')) {
      return 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20';
    }
    if (roleLower.includes('devops') || roleLower.includes('sre') || roleLower.includes('platform')) {
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    }
    if (roleLower.includes('designer') || roleLower.includes('design')) {
      return 'bg-pink-500/10 text-pink-600 border-pink-500/20';
    }
    if (roleLower.includes('qa') || roleLower.includes('test') || roleLower.includes('quality')) {
      return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
    }
    if (roleLower.includes('product')) {
      return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
    }
    if (roleLower.includes('developer') || roleLower.includes('engineer')) {
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    }
    
    // Default gray for unknown roles
    return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
  };

  const getLinkIcon = (type: string) => {
    const icons = {
      jira: SettingsIcon,
      confluence: FileText,
      github: GitBranch,
      slack: MessageSquare,
      wiki: BookOpen,
      documentation: FileText,
      other: Globe
    };
    const Icon = icons[type as keyof typeof icons] || Globe;
    return <Icon className="h-4 w-4" />;
  };

  const getLinkColor = (type: string) => {
    const colors = {
      jira: 'from-blue-500/20 to-blue-600/20 border-blue-500/30 hover:border-blue-500/50',
      confluence: 'from-indigo-500/20 to-indigo-600/20 border-indigo-500/30 hover:border-indigo-500/50',
      github: 'from-slate-500/20 to-slate-600/20 border-slate-500/30 hover:border-slate-500/50',
      slack: 'from-purple-500/20 to-purple-600/20 border-purple-500/30 hover:border-purple-500/50',
      wiki: 'from-green-500/20 to-green-600/20 border-green-500/30 hover:border-green-500/50',
      documentation: 'from-amber-500/20 to-amber-600/20 border-amber-500/30 hover:border-amber-500/50',
      other: 'from-gray-500/20 to-gray-600/20 border-gray-500/30 hover:border-gray-500/50'
    };
    return colors[type as keyof typeof colors] || colors.other;
  };

  const getLinkIconColor = (type: string) => {
    const colors = {
      jira: 'text-blue-600',
      confluence: 'text-indigo-600',
      github: 'text-slate-700',
      slack: 'text-purple-600',
      wiki: 'text-green-600',
      documentation: 'text-amber-600',
      other: 'text-gray-600'
    };
    return colors[type as keyof typeof colors] || colors.other;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/teams">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Teams
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{team.name}</h1>
            <p className="text-muted-foreground">{team.description}</p>
          </div>
        </div>
        {hasPermission('teams:update') && (
          <Button onClick={() => navigate(`/teams/${id}/edit`)} variant="outline" size="sm">
            <Edit className="h-4 w-4 mr-2" />
            Edit Team
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="relative overflow-hidden">
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Team Members</CardTitle>
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1">{team.members?.length || 0}</div>
            <p className="text-sm text-muted-foreground">Active members</p>
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden">
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Services</CardTitle>
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Package className="h-5 w-5 text-purple-600" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1">{teamServices.length || team.servicesCount || 0}</div>
            <p className="text-sm text-muted-foreground">Owned services</p>
          </CardContent>
        </Card>
        {team.lead && (
          <Card className="relative overflow-hidden">
            <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Team Lead</CardTitle>
              <div className="p-2 rounded-lg bg-amber-500/10">
                <UserCog className="h-5 w-5 text-amber-600" />
              </div>
            </CardHeader>
            <CardContent className="relative">
              <div className="text-2xl font-bold mb-1 truncate">{team.lead}</div>
              <p className="text-sm text-muted-foreground">Current lead</p>
            </CardContent>
          </Card>
        )}
        {team.email && (
          <Card className="relative overflow-hidden">
            <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Contact</CardTitle>
              <div className="p-2 rounded-lg bg-green-500/10">
                <Mail className="h-5 w-5 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <a href={`mailto:${team.email}`} className="text-sm font-medium hover:text-primary transition-colors truncate block">
                {team.email}
              </a>
              <p className="text-xs text-muted-foreground">Team email</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Main Content */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Team Information */}
        <Card>
          <CardHeader>
            <CardTitle>Team Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {team.tags && team.tags.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tags</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {team.tags.map((tag, index) => (
                    <Badge key={index} className="bg-primary/10 text-primary border-primary/20">
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {team.projects && team.projects.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Projects</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {team.projects.map((project, index) => (
                    <Badge key={index} className="bg-accent/50 text-accent-foreground border-accent">
                      <FolderKanban className="h-3 w-3 mr-1" />
                      {project}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created</label>
              <p className="text-sm mt-1">{new Date(team.createdAt).toLocaleDateString()}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
              <p className="text-sm mt-1">{new Date(team.updatedAt).toLocaleDateString()}</p>
            </div>
          </CardContent>
        </Card>

        {/* Team Members */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Team Members</CardTitle>
              {hasPermission('teams:update') && (
                <Button size="sm" onClick={() => setIsAddMemberModalOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Member
                </Button>
              )}
            </div>
            <CardDescription className="mt-1">
              {team.members?.length || 0} {(team.members?.length || 0) === 1 ? 'member' : 'members'} in this team
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(team.members?.length || 0) === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm font-medium">No team members yet</p>
                <p className="text-xs mt-1">Add your first team member</p>
              </div>
            ) : (
              <div className="space-y-2">
                {team.members?.map((member, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-3 rounded-lg border hover:border-primary/50 transition-colors cursor-pointer group"
                    onClick={() => openManageMemberModal(member)}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={member.avatar} alt={member.name} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{member.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                    </div>
                    <Badge variant="outline" className={`${getRoleColor(member.role)} flex-shrink-0 text-xs`}>
                      {member.role}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      {team.links && team.links.length > 0 && (
        <Card className="border-primary/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-primary" />
              Quick Links
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {team.links.map((link, idx) => (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative overflow-hidden flex items-center justify-between p-4 rounded-lg border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent hover:border-primary/50 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 text-primary group-hover:from-primary/30 group-hover:to-primary/20 transition-all">
                      {getLinkIcon(link.type)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{link.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{link.type}</p>
                    </div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Services */}
      <Card className="border-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Services
          </CardTitle>
          <CardDescription>
            {teamServices.length} {teamServices.length === 1 ? 'service' : 'services'} owned by this team
          </CardDescription>
        </CardHeader>
        <CardContent>
          {teamServices.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="h-16 w-16 mx-auto mb-4 opacity-20" />
              <p className="font-medium">No services yet</p>
              <p className="text-sm mt-1">Services will appear here when assigned to this team</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamServices.map((service) => (
                <Link
                  key={service.id}
                  to={`/services/${service.id}`}
                  className="relative overflow-hidden flex items-start gap-3 p-4 rounded-lg border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent hover:border-primary/50 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 text-primary group-hover:from-primary/30 group-hover:to-primary/20 transition-all">
                    <Package className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">
                      {service.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {service.description}
                    </p>
                    <Badge className="mt-2 bg-gradient-to-r from-secondary/80 to-secondary/60 text-secondary-foreground border-secondary">
                      {service.type}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <AddTeamMemberModal
        open={isAddMemberModalOpen}
        onOpenChange={setIsAddMemberModalOpen}
        existingMembers={team.members}
        onAddMember={handleAddMember}
      />
      
      <ManageTeamMemberModal
        open={manageMemberModalOpen}
        onOpenChange={setManageMemberModalOpen}
        member={selectedMember}
        onUpdateMember={handleUpdateMemberRole}
        onRemoveMember={handleRemoveMember}
      />
    </div>
  );
};
